'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["children", "className", "error", "focused", "hideEditIcon", "htmlFor", "isStatic", "label", "labelProps", "overlayContent", "readOnly", "required", "validationMessage"],
  _excluded2 = ["focused"],
  _excluded3 = ["hovered"];
import PropTypes from 'prop-types';
import { Children, cloneElement, Component } from 'react';
import { findDOMNode } from 'react-dom';
import classNames from 'classnames';
import * as colors from 'HubStyleTokens/colors';
import { memoizedSequence } from '../core/Functions';
import UIIcon from '../icon/UIIcon';
import UIFloatingFormLabel from '../form/UIFloatingFormLabel';
import { Small } from '../elements';
import UIDropdownCaret from '../dropdown/UIDropdownCaret';
import { hidden } from '../utils/propTypes/decorators';
import FocusProvider from '../providers/FocusProvider';
import HoverProvider from '../providers/HoverProvider';
import { uniqueId } from '../utils/underscore';
import { warnIfFragment } from '../utils/devWarnings';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const hiddenCaretRenderer = props => /*#__PURE__*/_jsx(UIDropdownCaret, Object.assign({}, props, {
  hidden: true
}));
const getChildProps = props => Children.only(props.children).props;
const getInputHasPlaceholder = children => {
  const childProps = Children.only(children).props;
  // The `format` prop check is special-casing for UIDateInput and UITimeInput
  return !!(childProps.placeholder || childProps.format);
};
const getInputHasValue = inputValue => {
  if (Array.isArray(inputValue)) {
    return inputValue.length > 0;
  } else if (typeof inputValue === 'number') {
    return true;
  }
  return Boolean(inputValue);
};
export default class UIFloatingFormControl extends Component {
  constructor(props) {
    super(props);
    this.updateHasClosedDropdown = () => {
      if (!this._isMounted) {
        return;
      }
      const newHasClosedDropdown = !!this._el.querySelector('[data-dropdown-open="false"]');
      if (this.state.hasClosedDropdown !== newHasClosedDropdown) {
        this.setState({
          hasClosedDropdown: newHasClosedDropdown
        });
      }
    };
    this.handleInputChange = evt => {
      this.setState({
        inputValue: evt.target.value
      });
    };
    this.handleLabelOnMouseDown = evt => {
      const {
        focused
      } = this.props;
      if (focused) evt.preventDefault();
    };
    this.handleOverlayBlur = () => {
      this.setState({
        overlayHasFocus: false
      });
    };
    this.handleOverlayFocus = () => {
      this.setState({
        overlayHasFocus: true
      });
    };
    this.handleEditIconClick = evt => {
      if (this._inputRef && typeof this._inputRef.focus === 'function') {
        evt.stopPropagation();
        this._inputRef.focus();
      }
    };
    this.state = {
      hasClosedDropdown: false,
      inputId: '',
      inputValue: getChildProps(props).defaultValue,
      overlayHasFocus: false
    };
  }
  static getDerivedStateFromProps(props, state) {
    const childProps = getChildProps(props);
    const inputId = childProps.id;
    const inputValue = childProps.value;
    return Object.assign({}, state, {
      inputId: inputId || state.inputId || uniqueId('floating-input-'),
      inputValue: inputValue !== undefined ? inputValue : state.inputValue
    });
  }
  componentDidMount() {
    this._isMounted = true;
    /* eslint-disable-next-line react/no-find-dom-node */
    this._el = findDOMNode(this);
    if (!this._el) return;
    this.updateHasClosedDropdown();
    this.startMutationObserver();
  }
  componentWillUnmount() {
    this._isMounted = false;
    this.stopMutationObserver();
  }
  startMutationObserver() {
    if (!this._el) return;
    this._observer = new MutationObserver(this.updateHasClosedDropdown);
    this._observer.observe(this._el, {
      attributes: true,
      subtree: true
    });
  }
  stopMutationObserver() {
    if (this._observer) {
      this._observer.disconnect();
      this._observer = null;
    }
  }

  // If you already have focus on the field, prevent the label click from doing anything
  // This uses onMouseDown instead of onClick because onClick triggers after onBlur of the input field
  renderClonedInput(children, error, focused, hasClosedDropdown, hasPlaceholder, hovered, inputHasValue, inputId, readOnly, required) {
    const child = Children.only(children);
    const childProps = child.props;

    // if the child input doesn't declare `readOnly`, pass it down
    const computedReadOnly = childProps.readOnly == null ? readOnly : childProps.readOnly;

    // if the child input doesn't declare `required`, pass it down
    const computedRequired = childProps.required == null ? required : childProps.required;
    const newError = error === true ? 'true' : undefined;
    const cloneProps = {
      className: classNames(childProps.className, 'private-form__control--inline', hasPlaceholder && !inputHasValue && 'private-form__control--inline--show-placeholder'),
      id: inputId,
      readOnly: computedReadOnly,
      required: computedRequired,
      'aria-invalid': newError,
      'aria-required': computedRequired,
      onChange: memoizedSequence(this.handleInputChange, childProps.onChange),
      ref: inputRef => {
        this._inputRef = inputRef;
      }
    };

    // only add error prop if there is an error, to avoid potential "Unknown prop" warnings
    if (error) {
      cloneProps.error = !!error;
    }

    // if the child has a closed dropdown, tell it not to render a caret
    if (hasClosedDropdown && !focused && !hovered) cloneProps.caretRenderer = hiddenCaretRenderer;
    warnIfFragment(child, UIFloatingFormControl.displayName);
    return /*#__PURE__*/cloneElement(child, cloneProps);
  }
  render() {
    const _this$props = this.props,
      {
        children,
        className,
        error,
        hideEditIcon,
        htmlFor,
        isStatic,
        label,
        labelProps,
        overlayContent,
        readOnly,
        required,
        validationMessage
      } = _this$props,
      rest = _objectWithoutPropertiesLoose(_this$props, _excluded);
    const {
      hasClosedDropdown,
      inputId,
      inputValue,
      overlayHasFocus
    } = this.state;
    return /*#__PURE__*/_jsx(HoverProvider, Object.assign({}, this.props, {
      children: hoverProviderProps => /*#__PURE__*/_jsx(FocusProvider, Object.assign({}, this.props, {
        children: focusProviderProps => {
          const {
              focused
            } = focusProviderProps,
            focusProviderRestProps = _objectWithoutPropertiesLoose(focusProviderProps, _excluded2);
          const {
              hovered
            } = hoverProviderProps,
            hoverProviderRestProps = _objectWithoutPropertiesLoose(hoverProviderProps, _excluded3);
          const generatedHtmlFor = htmlFor || inputId;
          const computedClassName = classNames("private-form__set private-form__set--floating", className, error && 'private-form__set--error', focused && 'private-form__set--floating--focused');
          const hasPlaceholder = getInputHasPlaceholder(children);
          const inputHasValue = getInputHasValue(inputValue);
          const renderedLabel = label && /*#__PURE__*/_jsx(UIFloatingFormLabel, Object.assign({}, labelProps, {
            floatActive: hovered || focused || inputHasValue || hasPlaceholder || isStatic,
            error: error,
            htmlFor: generatedHtmlFor,
            onMouseDown: this.handleLabelOnMouseDown,
            readOnly: readOnly,
            required: required,
            children: label
          }));
          const renderedInput = isStatic ? /*#__PURE__*/_jsx("div", {
            className: "private-form__control-floating__static-input-wrapper",
            children: children
          }) : this.renderClonedInput(children, error, focused, hasClosedDropdown, hasPlaceholder, hovered, inputHasValue, inputId, readOnly, required);
          return /*#__PURE__*/_jsxs("div", Object.assign({
            className: computedClassName
          }, rest, focusProviderRestProps, hoverProviderRestProps, {
            children: [renderedLabel, renderedInput, (overlayContent || !hideEditIcon) && /*#__PURE__*/_jsxs("div", {
              className: classNames('private-form__control__overlay', overlayHasFocus ? 'private-form__control__overlay--visible' : focused && 'private-form__control__overlay--hidden'),
              onFocus: this.handleOverlayFocus,
              onBlur: this.handleOverlayBlur,
              children: [hideEditIcon || readOnly ? null : /*#__PURE__*/_jsx(UIIcon, {
                className: "private-form__control-floating__edit-icon",
                color: colors.CALYPSO,
                name: "edit",
                onClick: this.handleEditIconClick,
                size: 12
              }), overlayContent]
            }), validationMessage && /*#__PURE__*/_jsx("div", {
              className: "private-form__validation-wrapper",
              children: /*#__PURE__*/_jsx(Small, {
                className: "private-form__validation",
                use: error ? 'error' : 'success',
                children: validationMessage
              })
            })]
          }));
        }
      }))
    }));
  }
}
UIFloatingFormControl.displayName = 'UIFloatingFormControl';
UIFloatingFormControl.defaultProps = {
  error: false,
  hideEditIcon: false,
  isStatic: false
};
UIFloatingFormControl.propTypes = {
  children: PropTypes.node.isRequired,
  error: PropTypes.bool,
  focused: hidden(PropTypes.bool),
  hideEditIcon: PropTypes.bool.isRequired,
  htmlFor: PropTypes.string,
  isStatic: PropTypes.bool.isRequired,
  overlayContent: PropTypes.node,
  label: PropTypes.node,
  labelProps: PropTypes.object,
  readOnly: PropTypes.bool,
  required: PropTypes.bool,
  validationMessage: PropTypes.node
};
UIFloatingFormControl.hiddenCaretRenderer = hiddenCaretRenderer;